import { ArticleOutlined, CheckCircleOutline, Close, DeleteOutlined, FileDownload, Info } from '@mui/icons-material'
import UploadIcon from '@mui/icons-material/Upload'
import { Box, Button, Modal } from '@mui/material'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import CustomTooltip from './custom-tooltip'
import { useStyles } from './styles'

interface IModalEditPj {
  open: boolean
  onClose: () => void
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  height: 'auto',
  minHeight: '367px',
  maxHeight: '800px',
  width: '100%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '0.35rem',
  border: 'none',
  outline: 'none',
  padding: '1rem 1.5rem 1.5rem 1.5rem',
}

const ModalEditPj: React.FC<IModalEditPj> = ({ onClose, open }) => {
  const [files, setFiles] = useState<File[]>([])
  const [alert, setAlert] = useState<boolean>(false)
  const [inputKey, setInputKey] = useState(Date.now())

  const classes = useStyles()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileInputClick = () => fileInputRef.current?.click()

  const handleDragOver = (e: any) => e.preventDefault()

  const handleFileSelect = (e: any) => {
    e.preventDefault()
    const selectedFiles: File[] = [...e.dataTransfer.files]

    setFiles((prevFiles) => [...prevFiles, ...selectedFiles])

    e.target.files = null
  }

  const handleRemoveFIle = (i: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, index) => index !== i))
    setInputKey(Date.now())
  }

  const handleAddFile = (e: ChangeEvent<HTMLInputElement>) => {
    const filesFromForm = e.target.files
    if (filesFromForm) {
      const fileListFromForm: File[] = [...filesFromForm]

      setFiles((prevFiles) => [...prevFiles, ...fileListFromForm])
    }

    e.target.files = null
  }

  const handleSubmitFiles = () => {
    setFiles([])
    setAlert(true)
  }

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (alert) {
      timer = setTimeout(() => setAlert(false), 5000)
    }

    return () => clearTimeout(timer)
  }, [alert])

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Box className={classes.header}>
          <Box className={classes.title}>
            <h6>Editar Pessoas Jurídicas</h6>
            <CustomTooltip
              title={
                <Box className={classes.tooltip}>
                  <span>Começe sempre por "Baixar tabela atual" e em seguida:</span>
                  <ul>
                    <li>Para inserir PJs: adicione ao arquivo baixado e suba o arquivo atualizado</li>
                    <li>Para modificar PJs: modifique o arquivo baixado e suba o arquivo atualizado</li>
                    <li>Para remover PJs: remova do arquivo baixado e suba o arquivo atualizado</li>
                  </ul>
                </Box>
              }
              placement='bottom'
              arrow
            >
              <Info
                color='disabled'
                className={classes.tooltipIcon}
              />
            </CustomTooltip>
          </Box>

          <Close
            className={classes.closeBtn}
            onClick={onClose}
          />
        </Box>

        <Box className={classes.subHeader}>
          <span>
            Faça upload de um arquivo Excel (
            <a
              target='_black'
              href=''
            >
              use este template
            </a>
            ) para adicionar, modificar ou remover pessoas jurídicas da base total de vidas.
          </span>
        </Box>

        <Button
          type='button'
          variant='outlined'
          className={classes.downloadButton}
        >
          <FileDownload
            fontSize='small'
            color='primary'
          />{' '}
          Baixar tabela atual
        </Button>

        <Box
          className={classes.uploadArea}
          onClick={handleFileInputClick}
          onDrop={handleFileSelect}
          onDragOver={handleDragOver}
        >
          <input
            type='file'
            name='file'
            accept='.xls, .xlsx'
            multiple
            hidden
            ref={fileInputRef}
            onChange={handleAddFile}
          />

          <UploadIcon />

          <span className={classes.uploadAreaTextSend}>Arrastar e soltar ou clique para procurar</span>

          <span className={classes.uploadAreaTextInfo}>Somente arquivos nos formatos xls e xlsx são aceitos</span>
        </Box>

        {alert && (
          <Box className={classes.alertBox}>
            <CheckCircleOutline
              color='success'
              fontSize='small'
            />
            <Box className={classes.alertText}>
              <strong>Arquivo enviado com sucesso</strong>
              <span>
                <a href=''>Clique aqui</a> para ver a lista atualizada de Pessoas Jurídicas.
              </span>
            </Box>
            <Close
              className={classes.alertClose}
              fontSize='small'
              onClick={() => setAlert(false)}
            />
          </Box>
        )}

        {files.length > 0 && (
          <Box className={classes.uploadFiles}>
            <strong>Arquivos carregados</strong>

            <Box className={classes.uploadFilesContainer}>
              {files.map((file, i) => (
                <Box
                  key={i}
                  className={classes.uploadFileItem}
                >
                  <Box className={classes.uploadFileItemContent}>
                    <ArticleOutlined className={classes.uploadFileItemArticleIcon} />
                    <span>{file.name}</span>
                    <DeleteOutlined
                      className={classes.uploadFileItemDeleteIcon}
                      onClick={() => handleRemoveFIle(i)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box className={classes.actions}>
          <Button
            type='button'
            variant='outlined'
            className={classes.cancelButton}
            onClick={() => {}}
          >
            Cancelar
          </Button>
          <Button
            type='submit'
            variant='contained'
            size='small'
            disabled={files.length === 0}
            className={classes.submitButton}
            onClick={handleSubmitFiles}
          >
            Enviar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalEditPj
